/**
 * Component: Dialogs
 * Description:
 * Fullscreen accessible <dialog> component.
 *
 * Usage:
 * dialogs(document.querySelectorAll('[data-dialog-trigger]'))
 *
 * Notes:
 * - Opens fullscreen modal
 * - Only closes via close button
 * - ESC closes (native dialog support)
 * - Restores focus to trigger
 * - Adds aria-expanded / aria-controls
 */

export function dialogs(triggers) {
    if (!triggers?.length) return;

    let lastFocusedElement = null;

    triggers.forEach((trigger) => {
        const targetId = trigger.dataset.dialogTrigger;
        const dialog = document.getElementById(`dialog-${targetId}`);

        if (!(dialog instanceof HTMLDialogElement)) return;

        const closeButton = dialog.querySelector(".dialog-close");

        trigger.setAttribute("aria-controls", dialog.id);
        trigger.setAttribute("aria-expanded", "false");

        trigger.addEventListener("click", () => openDialog(dialog, trigger));

        closeButton?.addEventListener("click", () => closeDialog(dialog));

        dialog.addEventListener("cancel", (event) => {
            event.preventDefault();
            closeDialog(dialog);
        });

        dialog.addEventListener("close", () => {
            trigger.setAttribute("aria-expanded", "false");

            if (lastFocusedElement instanceof HTMLElement) {
                lastFocusedElement.focus();
            }
        });
    });

    function openDialog(dialog, trigger) {
        if (dialog.open) return;

        lastFocusedElement = document.activeElement;
        dialog.showModal();
        trigger.setAttribute("aria-expanded", "true");
        lockPageScroll();
        getFocusable(dialog)?.focus();
    }

    function closeDialog(dialog) {
        if (!dialog.open) return;
        dialog.close();
        unlockPageScroll();
    }

    function getFocusable(dialog) {
        return dialog.querySelector(`
            button,
            [href],
            input,
            select,
            textarea,
            [tabindex]:not([tabindex="-1"])
        `);
    }

    function lockPageScroll() {
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
    }

    function unlockPageScroll() {
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
    }
}